import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Link,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useAtomValue } from 'jotai';
import { Avatar } from '../Avatar';
import { currentFamilyIdAtom } from '../../store';
import { Mail } from 'lucide-react';
import { Section, SettingRow } from './PageScaffold';
import { useBlocks } from '../../hooks';
import { useSiteConfig } from '../../lib/siteConfig';
import { ApiError } from '../../lib/api';
import { useT } from '../../i18n';

// Settings → Blocked people (M58, D208) and Settings → About (D209).

export function BlockedPeopleSection() {
  // Faces open through the family on screen, if they're in it (D292).
  const familyId = useAtomValue(currentFamilyIdAtom);
  const t = useT();
  const toast = useToast();
  const { blocks, unblock } = useBlocks();
  const [busy, setBusy] = useState<string | null>(null);

  const onUnblock = async (id: string, name: string) => {
    setBusy(id);
    try {
      await unblock(id);
      toast({
        title: t('account.blocked.unblocked', { name }),
        status: 'success',
      });
    } catch (err) {
      toast({
        title: t('account.blocked.failed'),
        description:
          err instanceof ApiError ? err.message : t('common.somethingWrong'),
        status: 'error',
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Section
      title={t('account.blocked.title')}
      description={t('account.blocked.description')}
    >
      {!blocks || blocks.length === 0 ? (
        <Text color="muted" fontSize="15px" py={2}>
          {t('account.blocked.none')}
        </Text>
      ) : (
        blocks.map(({ user }) => {
          const name = user.deleted
            ? t('account.blocked.deleted')
            : user.displayName;
          return (
            <HStack
              key={user.id}
              minH="56px"
              spacing={3}
              py={2}
              borderTopWidth="1px"
              borderColor="line"
              _first={{ borderTopWidth: 0 }}
            >
              <Avatar
                size="sm"
                name={name}
                src={user.avatarUrl ?? undefined}
                ownerId={user.id}
                familyId={familyId}
              />
              <Box flex="1" minW={0}>
                <Text fontSize="17px" fontWeight={600} noOfLines={1}>
                  {name}
                </Text>
                {user.handle && (
                  <Text fontSize="14px" color="muted" noOfLines={1}>
                    @{user.handle}
                  </Text>
                )}
              </Box>
              <Button
                h="40px"
                variant="outline"
                colorScheme="gray"
                borderColor="line"
                isLoading={busy === user.id}
                onClick={() => void onUnblock(user.id, name)}
              >
                {t('account.blocked.unblock')}
              </Button>
            </HStack>
          );
        })
      )}
    </Section>
  );
}

export function AboutSection() {
  const t = useT();
  const supportEmail = useSiteConfig()?.supportEmail ?? null;
  const policyLink = (to: string, label: string) => (
    <Link as={RouterLink} to={to} color="accent" fontWeight={700}>
      {label}
    </Link>
  );
  return (
    <Section title={t('account.about.title')}>
      {supportEmail && (
        <SettingRow
          label={t('account.about.contact')}
          hint={
            <>
              {t('account.about.contactHint')}{' '}
              <Link
                href={`mailto:${supportEmail}`}
                color="accent"
                fontWeight={700}
                wordBreak="break-all"
                data-testid="contact-twinden"
              >
                {supportEmail}
              </Link>
            </>
          }
        >
          <IconButton
            as="a"
            href={`mailto:${supportEmail}`}
            aria-label={t('account.about.contact')}
            icon={<Mail size={18} />}
            variant="outline"
            colorScheme="gray"
            borderColor="line"
          />
        </SettingRow>
      )}
      <Stack
        direction="row"
        spacing={5}
        flexWrap="wrap"
        pt={supportEmail ? 3 : 1}
        fontSize="15px"
      >
        {policyLink('/help', t('account.about.help'))}
        {policyLink('/privacy', t('account.about.privacy'))}
        {policyLink('/terms', t('account.about.terms'))}
        {policyLink('/contact', t('account.about.contactPage'))}
      </Stack>
    </Section>
  );
}
